import { useSelector, useDispatch } from "react-redux";
import { RootState } from "@funded-here-interface/investor-onboarding/src/store";
import { UploadedFile } from "../features/merchant/doc";
import * as docActions from "../features/merchant/doc";

/**
 * Document field configuration
 * Maps each document type to its Redux action
 */
const DOCUMENT_CONFIG = {
  balance: { action: docActions.setBalance, key: "balance" },
  business: { action: docActions.setBusinessDoc, key: "business" },
  proof: { action: docActions.setProofDoc, key: "proof" },
  share: { action: docActions.setShareDoc, key: "share" },
  bank: { action: docActions.setBankDoc, key: "bank" },
  salaryProof: { action: docActions.setSalaryProofDoc, key: "salaryProof" },
  financialStatement: {
    action: docActions.setFinancialStatementDoc,
    key: "financialStatement",
  },
  investmentMandate: {
    action: docActions.setInvestmentMandateDoc,
    key: "investmentMandate",
  },
  fundManagerLicense: {
    action: docActions.setFundManagerLicenseDoc,
    key: "fundManagerLicense",
  },
  certificateOfIncorporation: {
    action: docActions.setCertificateOfIncorporationDoc,
    key: "certificateOfIncorporation",
  },
  supportingDocumentsWithSuppliers: {
    action: docActions.setSupportingDocumentsWithSuppliersDoc,
    key: "supportingDocumentsWithSuppliers",
  },
  proofOfBankAccountDetails: {
    action: docActions.setProofOfBankAccountDetailsDoc,
    key: "proofOfBankAccountDetails",
  },
  crsSelfCertificationForm: {
    action: docActions.setCrsSelfCertificationFormDoc,
    key: "crsSelfCertificationForm",
  },
  companyConstitution: {
    action: docActions.setCompanyConstitutionDoc,
    key: "companyConstitution",
  },
} as const;

export type DocumentType = keyof typeof DOCUMENT_CONFIG;

/**
 * Generic hook for managing document uploads in Redux
 * Eliminates code duplication across all document types
 *
 * @param documentType - The type of document to manage
 * @returns Object with current documents array and append function
 */
export const useDocumentUpload = <T extends DocumentType>(documentType: T) => {
  const dispatch = useDispatch();
  const config = DOCUMENT_CONFIG[documentType];

  // Get documents from Redux state
  const documents = useSelector(
    (state: RootState) => state.bordoc[documentType] as UploadedFile[] | null
  );

  /**
   * Appends a new file to the document array
   * Handles null case by creating new array
   */
  const appendDocument = (
    newValue: UploadedFile,
    original: UploadedFile[] | null
  ) => {
    const updatedDocuments = [...(original ?? []), newValue];
    dispatch(config.action({ [config.key]: updatedDocuments } as any));
  };

  return {
    documents,
    appendDocument,
  };
};

// Export hooks with backward-compatible API
export const useAppendToBalance = () => {
  const { documents, appendDocument } = useDocumentUpload("balance");
  return {
    balance: documents,
    appendToBalance: appendDocument,
  };
};

export const useShareArray = () => {
  const { documents, appendDocument } = useDocumentUpload("share");
  return {
    share: documents,
    appendToShare: appendDocument,
  };
};

export const useBusiness = () => {
  const { documents, appendDocument } = useDocumentUpload("business");
  return {
    business: documents,
    appendToBusiness: appendDocument,
  };
};

export const useProofArray = () => {
  const { documents, appendDocument } = useDocumentUpload("proof");
  return {
    proof: documents,
    appendToProof: appendDocument,
  };
};

export const useBankArray = () => {
  const { documents, appendDocument } = useDocumentUpload("bank");
  return {
    bank: documents,
    appendToBank: appendDocument,
  };
};

export const useSalaryProofArray = () => {
  const { documents, appendDocument } = useDocumentUpload("salaryProof");
  return {
    salaryProof: documents,
    appendToSalaryProof: appendDocument,
  };
};

export const useFinancialStatementArray = () => {
  const { documents, appendDocument } = useDocumentUpload("financialStatement");
  return {
    financialStatement: documents,
    appendToFinancialStatement: appendDocument,
  };
};

export const useCertificateOfIncorporation = () => {
  const { documents, appendDocument } = useDocumentUpload(
    "certificateOfIncorporation"
  );
  return {
    certificateOfIncorporation: documents,
    appendToCertificateOfIncorporation: appendDocument,
  };
};

export const useSupportingDocumentsWithSuppliers = () => {
  const { documents, appendDocument } = useDocumentUpload(
    "supportingDocumentsWithSuppliers"
  );
  return {
    supportingDocumentsWithSuppliers: documents,
    appendToSupportingDocumentsWithSuppliers: appendDocument,
  };
};

export const useInvestmentMandateArray = () => {
  const { documents, appendDocument } = useDocumentUpload("investmentMandate");
  return {
    investmentMandate: documents,
    appendToInvestorMandate: appendDocument,
  };
};

export const useFundManagerLicenseArray = () => {
  const { documents, appendDocument } = useDocumentUpload("fundManagerLicense");
  return {
    fundManagerLicense: documents,
    appendToFundManagerLicense: appendDocument,
  };
};

export const useProofOfBankAccountDetailsArray = () => {
  const { documents, appendDocument } = useDocumentUpload(
    "proofOfBankAccountDetails"
  );
  return {
    proofOfBankAccountDetails: documents,
    appendToProofOfBankAccountDetails: appendDocument,
  };
};

export const useCrsSelfCertificationFormArray = () => {
  const { documents, appendDocument } = useDocumentUpload(
    "crsSelfCertificationForm"
  );
  return {
    crsSelfCertificationForm: documents,
    appendToCrsSelfCertificationForm: appendDocument,
  };
};

export const useCompanyConstitutionArray = () => {
  const { documents, appendDocument } = useDocumentUpload(
    "companyConstitution"
  );
  return {
    companyConstitution: documents,
    appendToCompanyConstitution: appendDocument,
  };
};
